import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  email?: string;
  letterhead?: string;
}

interface Customer {
  id: number;
  company_name: string;
  address?: string;
  city?: string;
  state?: string;
  gst_number?: string;
  email?: string;
}

const QuotationCreator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const quotationId = searchParams.get('quotation_id');
  const isEditMode = quotationId && quotationId !== 'new';

  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [quotationNo, setQuotationNo] = useState('');
  const [date, setDate] = useState(new Date().toISOString().substring(0, 10));
  const [totalAmount, setTotalAmount] = useState('0');
  const [particulars, setParticulars] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  const [letterheadUrl, setLetterheadUrl] = useState('');
  const [uploadingLetterhead, setUploadingLetterhead] = useState(false);

  // Template System States
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateNameSave, setTemplateNameSave] = useState('');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);

  // New Customer Modal States
  const [showAddCustModal, setShowAddCustModal] = useState(false);
  const [newCustName, setNewCustName] = useState('');
  const [newCustEmail, setNewCustEmail] = useState('');
  const [newCustGST, setNewCustGST] = useState('');
  const [newCustPAN, setNewCustPAN] = useState('');
  const [newCustAddress, setNewCustAddress] = useState('');
  const [newCustCity, setNewCustCity] = useState('');
  const [newCustState, setNewCustState] = useState('');

  const insertPageBreak = () => {
    const pageBreakHtml = `<div class="print-page-break" style="page-break-after: always; break-after: page; border-top: 2px dashed #3b82f6; padding: 12px 0; text-align: center; color: #3b82f6; font-weight: bold; font-size: 12px; margin: 15px 0; user-select: none;" contenteditable="false">[ Page Break for Print / Multiple Pages ]</div>`;
    setScopeOfWork(prev => prev + pageBreakHtml);
  };

  const handleLetterheadUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadingLetterhead(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/auth/upload-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data && res.data.file_url) {
          setLetterheadUrl(res.data.file_url);
        }
      } catch (err) {
        alert("Failed to upload letterhead image.");
      } finally {
        setUploadingLetterhead(false);
      }
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  // Autofill letterhead inside Quill editor when company is chosen
  useEffect(() => {
    if (!isEditMode && selectedCompanyId) {
      const comp = companies.find(c => String(c.id) === String(selectedCompanyId));
      if (comp) {
        if (comp.letterhead && !letterheadUrl) {
          setLetterheadUrl(comp.letterhead);
        }
        const letterheadTemplate = `
<div style="text-align: center; border-bottom: 2px solid #1e3a8a; padding-bottom: 10px; margin-bottom: 20px; font-family: sans-serif;">
  <h1 style="color: #1e3a8a; font-size: 24px; margin: 0; text-transform: uppercase; font-weight: bold;">${comp.company_name}</h1>
  <p style="margin: 3px 0; font-size: 11px; color: #475569;">${comp.address || ''}, ${comp.city || ''}, ${comp.state || ''}</p>
  <p style="margin: 3px 0; font-size: 11px; color: #475569;">Email: ${comp.email || ''} | GSTIN: ${comp.gst_number || ''} | PAN: ${comp.pancard || ''}</p>
</div>
<br/>
<h3 style="color: #0f172a; font-weight: bold; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px;">Detailed Scope of Work & Terms</h3>
<p>Provide specific project parameters, terms of service, and timeline deliverables here...</p>
`;
        setScopeOfWork(letterheadTemplate);
      }
    }
  }, [selectedCompanyId, companies, isEditMode, letterheadUrl]);

  const handleLoadTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const temp = templates.find(t => String(t.id) === String(templateId));
    if (temp) {
      setParticulars(temp.particulars || '');
      setScopeOfWork(temp.scope_of_work || '');
      setRemarks(temp.remarks || '');
      setTotalAmount(String(temp.total_amount || '0'));
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateNameSave.trim()) {
      alert("Please enter a name for the template.");
      return;
    }
    try {
      const payload = {
        template_name: templateNameSave,
        particulars,
        scope_of_work: scopeOfWork,
        remarks,
        total_amount: Number(totalAmount)
      };
      await axios.post(`${API_URL}/auth/quotation-template`, payload);
      alert("Template saved successfully!");
      setShowSaveTemplateModal(false);
      setTemplateNameSave('');
      
      // Reload templates list
      const res = await axios.get(`${API_URL}/auth/quotation-templates`);
      setTemplates(res.data || []);
    } catch (err) {
      alert("Failed to save template.");
    }
  };

  const fetchMeta = async () => {
    try {
      const [compRes, custRes, tempRes] = await Promise.all([
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/customers`),
        axios.get(`${API_URL}/auth/quotation-templates`)
      ]);
      setCompanies(compRes.data || []);
      setCustomers(custRes.data || []);
      setTemplates(tempRes.data || []);

      if (isEditMode && quotationId) {
        setLoading(true);
        const res = await axios.get(`${API_URL}/auth/quotation/${quotationId}`);
        const q = res.data;
        setSelectedCompanyId(String(q.Company_id || ''));
        setSelectedCustomerId(String(q.Customer_id || ''));
        setQuotationNo(q.QuotationNo || '');
        if (q.Date) setDate(q.Date.substring(0, 10));
        setTotalAmount(String(q.Total_Amount || '0'));
        setParticulars(q.Particulars || '');
        setScopeOfWork(q.scope_of_work || '');
        setRemarks(q.remarks || '');
        setLetterheadUrl(q.letterhead_url || '');
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to load metadata in QuotationCreator:", err);
    }
  };

  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompanyId || !selectedCustomerId || !quotationNo || !date || !totalAmount) {
      alert("Please fill all required fields.");
      return;
    }
    if (saving) return;
    setSaving(true);
    const payload = {
      Company_id: Number(selectedCompanyId),
      Customer_id: Number(selectedCustomerId),
      QuotationNo: quotationNo,
      Date: date,
      Total_Amount: Number(totalAmount),
      Particulars: particulars,
      scope_of_work: scopeOfWork,
      remarks: remarks,
      letterhead_url: letterheadUrl
    };

    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/auth/quotation/${quotationId}`, payload);
      } else {
        await axios.post(`${API_URL}/auth/quotation`, payload);
      }
      navigate('/quotations');
    } catch (err) {
      alert("Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const handleAddCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustName.trim()) {
      alert("Customer Name is required.");
      return;
    }
    if (!selectedCompanyId) {
      alert("Please select a company first before adding a customer.");
      return;
    }
    try {
      const payload = {
        company_name: newCustName,
        company_id: Number(selectedCompanyId),
        email: newCustEmail,
        gst_number: newCustGST,
        pancard: newCustPAN,
        address: newCustAddress,
        city: newCustCity,
        state: newCustState,
        country: 'India',
        estimated_incoming: 0
      };
      const res = await axios.post(`${API_URL}/auth/customer`, payload);
      alert("Customer added successfully!");
      
      // refresh customers list
      const custRes = await axios.get(`${API_URL}/auth/customers`);
      const updatedCustomers = custRes.data || [];
      setCustomers(updatedCustomers);
      
      // Auto select the new customer
      if (res.data && res.data.id) {
        setSelectedCustomerId(String(res.data.id));
      } else {
        const match = updatedCustomers.find((c: any) => c.company_name === newCustName);
        if (match) setSelectedCustomerId(String(match.id));
      }

      // Reset fields
      setNewCustName('');
      setNewCustEmail('');
      setNewCustGST('');
      setNewCustPAN('');
      setNewCustAddress('');
      setNewCustCity('');
      setNewCustState('');
      setShowAddCustModal(false);
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to add customer.");
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500 font-medium">Loading quotation...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-extrabold text-blue-900 mb-6">{isEditMode ? '📝 Edit Quotation' : '➕ Create Quotation & Scope'}</h2>

      <form onSubmit={handleSave} className="bg-white p-6 rounded-3xl shadow-sm border border-blue-50 space-y-6 text-left">
        {/* Templates Selection & Saving block */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-5 mb-2 flex flex-col md:flex-row justify-between items-end md:items-center gap-4">
          <div className="flex-1 w-full text-left">
            <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-indigo-900/60 font-semibold">Load Frequently Used Sample/Template</label>
            <select
              value={selectedTemplateId}
              onChange={e => handleLoadTemplate(e.target.value)}
              className="w-full border-2 border-indigo-100 rounded-xl bg-white p-2.5 font-semibold text-indigo-950 focus:border-indigo-400 focus:outline-none"
            >
              <option value="">-- Select a Sample Template --</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.template_name} (₹{Number(t.total_amount).toLocaleString('en-IN')})</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => setShowSaveTemplateModal(true)}
            className="w-full md:w-auto px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow transition uppercase tracking-wider shrink-0"
          >
            💾 Save Current as Template
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Select Company *</label>
            <select
              value={selectedCompanyId}
              onChange={e => setSelectedCompanyId(e.target.value)}
              className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
              required
            >
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-gray-700 text-xs font-bold uppercase text-blue-900/60 font-semibold">Select Customer *</label>
              <button
                type="button"
                onClick={() => {
                  if (!selectedCompanyId) {
                    alert("Please select a company first.");
                    return;
                  }
                  setShowAddCustModal(true);
                }}
                className="text-[10px] bg-sky-100 hover:bg-sky-200 text-sky-700 px-2 py-0.5 rounded font-bold uppercase transition"
              >
                ➕ New Customer
              </button>
            </div>
            <select
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
              required
            >
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Quotation Number *</label>
            <input
              type="text"
              value={quotationNo}
              onChange={e => setQuotationNo(e.target.value)}
              className="w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Quotation Date *</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Total Amount (₹) *</label>
            <input
              type="number"
              value={totalAmount}
              onChange={e => setTotalAmount(e.target.value)}
              className="w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Brief Particulars</label>
            <input
              type="text"
              value={particulars}
              onChange={e => setParticulars(e.target.value)}
              className="w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
            />
          </div>
        </div>



        {/* React Quill Editor for Pasted Data Formatting Preservation */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-gray-700 text-xs font-bold uppercase text-blue-900/60 font-semibold">Detailed Scope of Work & Terms (Supports Rich Copy-Paste)</label>
            <button
              type="button"
              onClick={insertPageBreak}
              className="px-3 py-1 bg-sky-100 hover:bg-sky-200 text-sky-700 font-bold text-[10px] rounded-lg uppercase tracking-wider transition"
            >
              ✂ Insert Print Page Break
            </button>
          </div>
          <div className="border-2 border-blue-100 rounded-xl overflow-hidden bg-white">
            <ReactQuill
              theme="snow"
              value={scopeOfWork}
              onChange={setScopeOfWork}
              placeholder="Paste Scope document or edit terms directly here..."
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline','strike', 'blockquote'],
                  [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                  ['table'],
                  ['clean']
                ],
                table: true
              }}
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Remarks</label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            rows={3}
            className="w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
          />
        </div>

        <div className="flex gap-4 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/quotations')}
            className="px-5 py-2.5 border rounded-xl text-gray-500 hover:bg-gray-100 font-bold uppercase transition text-xs"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold uppercase transition text-xs shadow-md disabled:opacity-50 flex items-center gap-2"
          >
            {saving && (
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {saving ? 'Saving...' : 'Save Quotation'}
          </button>
        </div>
      </form>

      {/* Add Customer Modal Overlay */}
      {showAddCustModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full border border-blue-50 shadow-2xl relative text-left">
            <button
              type="button"
              onClick={() => setShowAddCustModal(false)}
              className="absolute right-6 top-6 text-slate-400 hover:text-slate-600 text-2xl font-bold"
            >
              &times;
            </button>
            <h3 className="font-extrabold text-xl text-indigo-950 mb-6 border-b pb-3">
              ➕ Add New Customer
            </h3>
            <form onSubmit={handleAddCustomerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Customer Name *</label>
                <input
                  type="text"
                  required
                  value={newCustName}
                  onChange={e => setNewCustName(e.target.value)}
                  className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none font-semibold text-slate-800"
                  placeholder="e.g. Acme Corporation"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                  <input
                    type="email"
                    value={newCustEmail}
                    onChange={e => setNewCustEmail(e.target.value)}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none text-slate-800"
                    placeholder="e.g. billing@acme.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">GST Number</label>
                  <input
                    type="text"
                    value={newCustGST}
                    onChange={e => setNewCustGST(e.target.value)}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none text-slate-800"
                    placeholder="e.g. 07AAAAA1111A1Z1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">PAN Card</label>
                  <input
                    type="text"
                    value={newCustPAN}
                    onChange={e => setNewCustPAN(e.target.value)}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none text-slate-800"
                    placeholder="e.g. ABCDE1234F"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">City</label>
                  <input
                    type="text"
                    value={newCustCity}
                    onChange={e => setNewCustCity(e.target.value)}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none text-slate-800"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">State</label>
                  <input
                    type="text"
                    value={newCustState}
                    onChange={e => setNewCustState(e.target.value)}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none text-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Country</label>
                  <input
                    type="text"
                    disabled
                    value="India"
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm text-slate-400 bg-slate-50"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Full Billing Address</label>
                <textarea
                  rows={2}
                  value={newCustAddress}
                  onChange={e => setNewCustAddress(e.target.value)}
                  className="w-full border-2 border-slate-100 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none text-slate-800"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddCustModal(false)}
                  className="px-5 py-2.5 border rounded-xl text-gray-500 hover:bg-gray-100 font-bold uppercase transition text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold uppercase transition text-xs shadow-md"
                >
                  Save Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md p-6 relative border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Save Quotation as Template</h3>
            <p className="text-xs text-gray-500 mb-4">Give this template a name so you can easily load these details (scope, terms, total amount) in the future.</p>
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Template Name *</label>
              <input
                type="text"
                required
                value={templateNameSave}
                onChange={e => setTemplateNameSave(e.target.value)}
                className="w-full border-2 border-slate-100 rounded-xl px-4 py-2.5 text-sm focus:border-indigo-300 focus:outline-none font-semibold text-slate-800"
                placeholder="e.g. Standard Web Development Template"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setShowSaveTemplateModal(false);
                  setTemplateNameSave('');
                }}
                className="px-5 py-2.5 border rounded-xl text-gray-500 hover:bg-gray-100 font-bold uppercase transition text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTemplate}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold uppercase transition text-xs shadow-md"
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationCreator;
